import React, { useEffect, useRef, useState } from "react";
import { inputClass, labelClass } from "./DashboardUI";

// Shroff receipt scan picker shared by every "Confirm Payment" form (member/
// club registration, membership/club renewal, ID card reprint, staff booking)
// and the Manual Approvals receipt backfill. Every */manual-confirm route
// now requires this file — the limits below mirror the backend's
// ManualPaymentReceiptFile (mimes:pdf,jpg,jpeg,png,webp, max:5120 KB), so a
// bad pick is caught here instead of round-tripping for a 422.
export const RECEIPT_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "webp"];
export const RECEIPT_MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = RECEIPT_EXTENSIONS.map((e) => `.${e}`).join(",");

// Returns an error message, or null if the file is acceptable.
export function validateReceiptFile(file) {
  if (!file) return "Please attach the payment receipt.";
  const ext = String(file.name || "").split(".").pop().toLowerCase();
  if (!RECEIPT_EXTENSIONS.includes(ext)) return "Receipt must be a PDF, JPG, PNG or WEBP file.";
  if (file.size > RECEIPT_MAX_BYTES) return "Receipt must be 5MB or smaller.";
  return null;
}

const fmtSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Controlled by `file` (a File or null): passing null clears the native input
// too, so a parent's form reset doesn't leave a stale filename showing.
// `error` is the server-side field error (e.g. fieldError("receipt")).
export default function ReceiptFileInput({ file, onChange, error, label = "Payment receipt", disabled = false }) {
  const inputRef = useRef(null);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!file && inputRef.current) inputRef.current.value = "";
  }, [file]);

  const handleChange = (e) => {
    const picked = e.target.files?.[0] ?? null;
    if (!picked) {
      setLocalError("");
      onChange(null);
      return;
    }
    const msg = validateReceiptFile(picked);
    setLocalError(msg || "");
    onChange(msg ? null : picked);
  };

  const shownError = localError || error;

  return (
    <div>
      <label className={labelClass}>
        {label} <span className="text-gray-400 font-normal">(PDF or image, max 5MB)</span>
      </label>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className={inputClass}
        onChange={handleChange}
        disabled={disabled}
        required
      />
      {file && !shownError && (
        <p className="text-xs text-gray-500 mt-1 truncate">
          {file.name} · {fmtSize(file.size)}
        </p>
      )}
      {shownError && <p className="text-xs text-red-600 mt-1">{shownError}</p>}
    </div>
  );
}
